import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "./Card";
import api from "../api/client";
import { X, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { fmtDate } from "../hooks/useClock";

export function RegularizationModal({ empId, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  
  const [regularizeForm, setRegularizeForm] = useState(null); // { eventId, manualTime, reason }

  useEffect(() => {
    fetchEvents();
  }, [empId]);

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/pi/attendance/${empId}`);
      if (res.data && res.data.data) {
        setEvents(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegularize = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/pi/regularize', {
        eventId: regularizeForm.eventId,
        manualTime: regularizeForm.manualTime,
        reason: regularizeForm.reason,
        managerId: 'manager' // Typically fetched from currentUser in context
      });
      setRegularizeForm(null);
      fetchEvents();
    } catch (err) {
      alert("Failed to regularize: " + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(10, 25, 50, 0.5)", backdropFilter: "blur(4px)",
      zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center",
      padding: "20px 0 0 0"
    }}>
      <div style={{
        background: "#F8F9FA", width: "100%", maxWidth: 600, height: "85vh",
        borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: "20px 20px",
        overflowY: "auto", position: "relative",
        boxShadow: "0 -10px 40px rgba(0,0,0,0.15)",
        animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 16, border: "none", background: "#fff",
          borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center",
          justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}>
          <X size={18} color={TOKENS.navyDeep} />
        </button>

        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 16 }}>
          Attendance Regularization
        </h2>

        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: TOKENS.muted, fontSize: 13 }}>Loading attendance events...</div>
        ) : events.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: TOKENS.muted, fontSize: 13 }}>No recent attendance events found.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {events.map(ev => {
              const isDenied = ev.gps_status === 'DENIED';
              const isUnavail = ev.gps_status === 'UNAVAILABLE';
              const isVerified = ev.gps_status === 'VERIFIED';
              const needsRegularization = (isDenied || isUnavail) && !ev.is_regularized;
              
              return (
                <Card key={ev.id} style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: TOKENS.muted, textTransform: "uppercase" }}>
                        {ev.event_type.toUpperCase()} · {fmtDate(ev.event_date)}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: TOKENS.navyDeep, marginTop: 4 }}>
                        {ev.event_time}
                      </div>
                    </div>
                    
                    <div style={{
                      padding: "4px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                      background: isVerified ? TOKENS.successBg : isDenied ? TOKENS.dangerBg : TOKENS.warningBg,
                      color: isVerified ? TOKENS.success : isDenied ? TOKENS.danger : TOKENS.warning
                    }}>
                      {ev.gps_status}
                    </div>
                  </div>

                  {ev.is_regularized ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: TOKENS.success, fontWeight: 600, background: TOKENS.successBg, padding: "6px 10px", borderRadius: 8, marginTop: 10 }}>
                      <CheckCircle2 size={14} /> Regularized to {ev.manual_time_override}
                    </div>
                  ) : needsRegularization ? (
                    <div style={{ marginTop: 10, borderTop: `1px dashed ${TOKENS.border}`, paddingTop: 10 }}>
                      {regularizeForm?.eventId === ev.id ? (
                        <form onSubmit={handleRegularize} style={{ background: TOKENS.cream, padding: 12, borderRadius: 12 }}>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Approved Time</label>
                            <input 
                              type="time" 
                              required 
                              value={regularizeForm.manualTime}
                              onChange={e => setRegularizeForm({ ...regularizeForm, manualTime: e.target.value })}
                              style={{ width: "100%", padding: "8px", borderRadius: 8, border: `1px solid ${TOKENS.border}` }}
                            />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Manager Reason</label>
                            <input 
                              type="text" 
                              required 
                              placeholder="e.g. Device issue verified"
                              value={regularizeForm.reason}
                              onChange={e => setRegularizeForm({ ...regularizeForm, reason: e.target.value })}
                              style={{ width: "100%", padding: "8px", borderRadius: 8, border: `1px solid ${TOKENS.border}` }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" onClick={() => setRegularizeForm(null)} style={{ flex: 1, padding: "8px", background: "#fff", border: `1px solid ${TOKENS.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12, color: TOKENS.muted, cursor: "pointer" }}>
                              Cancel
                            </button>
                            <button type="submit" disabled={submitting} style={{ flex: 1, padding: "8px", background: TOKENS.navyDeep, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, color: "#fff", cursor: submitting ? "wait" : "pointer" }}>
                              {submitting ? "Approving..." : "Approve Override"}
                            </button>
                          </div>
                        </form>
                      ) : (
                        <button 
                          onClick={() => setRegularizeForm({ eventId: ev.id, manualTime: ev.event_time, reason: "" })}
                          style={{ width: "100%", padding: "8px", background: TOKENS.navyDeep, border: "none", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                        >
                          <Clock size={14} /> Regularize Missing GPS
                        </button>
                      )}
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
